import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight, Menu, X } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_jardins-mr/artifacts/1m1faken_Captura%20de%20ecra%CC%83%202026-05-09%2C%20a%CC%80s%2018.50.12-Photoroom.png";

/* ---------- Static content ---------- */

const NAV_LINKS = [
  { label: "Serviços", href: "#servicos", testid: "nav-link-servicos" },
  { label: "Portfólio", href: "#portfolio", testid: "nav-link-portfolio" },
  { label: "Sobre", href: "#sobre", testid: "nav-link-sobre" },
  { label: "Contactos", href: "#contactos", testid: "nav-link-contactos" },
];

const SERVICES = [
  {
    id: "01",
    title: "Manutenção de jardins",
    desc: "Cuidado contínuo e especializado para manter a vitalidade e a beleza orgânica do seu espaço exterior, em todas as estações.",
    image:
      "https://customer-assets.emergentagent.com/job_jardins-mr/artifacts/999brzcy_543187857_122149543004796527_5400451243255376813_n.jpg",
  },
  {
    id: "02",
    title: "Paisagismo & Projetos",
    desc: "Desenho de exteriores e planeamento paisagístico feito à medida da arquitetura e da luz portuguesa.",
    image:
      "https://customer-assets.emergentagent.com/job_jardins-mr/artifacts/ih3bfer5_482246397_122100089786796527_8328319134175160142_n.jpg",
  },
  {
    id: "03",
    title: "Poda de árvores e arbustos",
    desc: "Poda técnica, estética e estrutural para garantir um crescimento equilibrado, saudável e duradouro.",
    image:
      "https://customer-assets.emergentagent.com/job_jardins-mr/artifacts/bila5edk_511305569_122136365960796527_4466161066454220470_n.jpg",
  },
  {
    id: "04",
    title: "Relvado & sistemas de rega",
    desc: "Instalação de relvado com soluções de rega sustentáveis, eficientes e silenciosas, pensadas para durar.",
    image:
      "https://customer-assets.emergentagent.com/job_jardins-mr/artifacts/m4b51m6j_569022468_122157381734796527_6170364647614693844_n.jpg",
  },
];

const PORTFOLIO = [
  {
    src: "https://customer-assets.emergentagent.com/job_jardins-mr/artifacts/75n2hsfq_487115127_122115001526796527_7930584977205615675_n.jpg",
    title: "Palmeiras esculpidas",
    place: "Distrito de Évora",
    span: "md:col-span-7 md:row-span-2",
    ratio: "aspect-[4/5] md:aspect-auto md:h-full",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_jardins-mr/artifacts/ih3bfer5_482246397_122100089786796527_8328319134175160142_n.jpg",
    title: "Oliveira em vaso",
    place: "Évora",
    span: "md:col-span-5",
    ratio: "aspect-[4/3]",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_jardins-mr/artifacts/bila5edk_511305569_122136365960796527_4466161066454220470_n.jpg",
    title: "Oliveira topiada",
    place: "Reguengos de Monsaraz",
    span: "md:col-span-5",
    ratio: "aspect-[4/3]",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_jardins-mr/artifacts/999brzcy_543187857_122149543004796527_5400451243255376813_n.jpg",
    title: "Jardim residencial com piscina",
    place: "Distrito de Évora",
    span: "md:col-span-12",
    ratio: "aspect-[21/9]",
  },
];

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_jardins-mr/artifacts/75n2hsfq_487115127_122115001526796527_7930584977205615675_n.jpg";

/* ---------- Helpers ---------- */

const WHATSAPP_URL = "https://wa.me/351968471110";
const PHONE_HREF = "tel:+351968471110";
const EMAIL_HREF = "mailto:jardim.mr@hotmail.com";

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ---------- Sections ---------- */

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-bone/85 backdrop-blur-md border-b border-hairline"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="brand-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO_URL}
            alt="MRjardins logo"
            className="w-11 h-11 md:w-12 md:h-12 object-contain transition-transform duration-500 group-hover:scale-105"
          />
          <span className="font-serif text-2xl md:text-[26px] tracking-tight text-ink">
            MR<span className="italic text-olive">jardins</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              className="relative text-sm uppercase tracking-widest2 text-inkSoft hover:text-ink transition-colors duration-300 group"
            >
              {l.label}
              <span className="absolute left-0 -bottom-1 w-0 h-px bg-ink transition-all duration-500 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="header-whatsapp-cta"
          className="hidden md:inline-flex items-center gap-2 text-sm tracking-wide text-ink border border-ink/80 px-5 py-2.5 hover:bg-ink hover:text-bone transition-all duration-500"
        >
          Falar agora
          <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
        </a>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Abrir menu"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 text-ink"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-menu"
        className={`md:hidden overflow-hidden bg-bone border-b border-hairline transition-[max-height] duration-500 ${
          open ? "max-h-96" : "max-h-0"
        }`}
      >
        <nav className="px-6 py-6 flex flex-col gap-5">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`mobile-${l.testid}`}
              onClick={() => setOpen(false)}
              className="text-sm uppercase tracking-widest2 text-inkSoft"
            >
              {l.label}
            </a>
          ))}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-whatsapp-cta"
            className="inline-flex items-center gap-2 text-sm tracking-wide text-ink border border-ink/80 px-5 py-2.5 w-fit"
          >
            Falar agora <ArrowUpRight className="w-4 h-4" />
          </a>
        </nav>
      </div>
    </header>
  );
};

const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative pt-32 md:pt-40 pb-16 md:pb-24">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <p
        data-testid="hero-overline"
        className="text-xs sm:text-sm uppercase tracking-widest2 text-muteText animate-fade-up delay-100"
      >
        Évora · Cobertura Nacional
      </p>

      <h1
        data-testid="hero-headline"
        className="font-serif mt-6 text-[44px] leading-[0.95] sm:text-6xl md:text-7xl lg:text-[104px] text-ink tracking-tight animate-fade-up delay-200"
      >
        Espaços exteriores
        <br />
        <span className="italic font-normal text-olive">elegantes</span>, pensados
        <br />
        ao detalhe.
      </h1>

      <div className="mt-10 grid md:grid-cols-12 gap-8 items-end">
        <p
          data-testid="hero-subheadline"
          className="md:col-span-6 md:col-start-7 text-base sm:text-lg font-light leading-relaxed text-inkSoft max-w-xl animate-fade-up delay-300"
        >
          Manutenção, paisagismo e desenho de jardins. Criamos ambientes
          intemporais, em harmonia com a luz e o ritmo de cada paisagem.
        </p>
      </div>

      <div className="mt-12 flex flex-wrap items-center gap-6 animate-fade-up delay-400">
        <a
          href="#portfolio"
          data-testid="hero-cta-portfolio"
          className="inline-flex items-center gap-3 bg-ink text-bone px-8 py-4 text-sm uppercase tracking-widest2 hover:bg-inkSoft transition-colors duration-500"
        >
          Ver portfólio
          <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
        </a>
        <a
          href="#contactos"
          data-testid="hero-cta-contact"
          className="text-sm uppercase tracking-widest2 text-ink border-b border-ink/40 pb-1 hover:border-ink transition-all"
        >
          Pedir orçamento
        </a>
      </div>
    </div>

    {/* Hero Video */}
    <div className="mt-20 md:mt-28 max-w-[1400px] mx-auto px-6 md:px-12">
      <div
        data-testid="hero-image-wrapper"
        className="relative w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden animate-fade-up delay-500 bg-ink"
      >
        <video
          src="/media/mrjardins-hero.mp4"
          poster="/media/mrjardins-hero-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-full object-cover photo-edit"
        />
      </div>
      <div className="mt-6 flex items-center justify-between text-xs uppercase tracking-widest2 text-muteText">
        <span>Estúdio MRjardins</span>
        <span>Desde 2020</span>
      </div>
    </div>
  </section>
);

const Servicos = () => {
  useReveal();
  return (
    <section
      id="servicos"
      data-testid="services-section"
      className="py-24 md:py-40 border-t border-hairline"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-24 reveal">
          <p className="md:col-span-3 text-xs uppercase tracking-widest2 text-muteText">
            (01) Serviços
          </p>
          <h2
            data-testid="services-title"
            className="md:col-span-9 font-serif text-4xl sm:text-5xl md:text-6xl text-ink tracking-tight leading-[1.02]"
          >
            Trabalho artesanal,
            <br />
            <span className="italic text-olive">olhar paciente</span> sobre a natureza.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-hairline border border-hairline">
          {SERVICES.map((s, i) => (
            <article
              key={s.id}
              data-testid={`service-card-${s.id}`}
              className="group bg-bone p-8 md:p-12 reveal"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="flex items-start justify-between mb-10">
                <span className="font-serif text-3xl md:text-4xl text-olive italic">
                  {s.id}
                </span>
                <ArrowUpRight
                  className="w-5 h-5 text-ink opacity-40 group-hover:opacity-100 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
                  strokeWidth={1.4}
                />
              </div>

              <div className="overflow-hidden mb-8 aspect-[16/10]">
                <img
                  src={s.image}
                  alt={s.title}
                  className="w-full h-full object-cover photo-edit transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                />
              </div>

              <h3
                data-testid={`service-title-${s.id}`}
                className="font-serif text-2xl md:text-[32px] text-ink leading-tight mb-4"
              >
                {s.title}
              </h3>
              <p className="text-base md:text-[17px] font-light leading-relaxed text-inkSoft max-w-md">
                {s.desc}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const Portfolio = () => {
  useReveal();
  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="py-24 md:py-40 bg-[#F3F1EC] border-t border-hairline"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-24 reveal">
          <p className="md:col-span-3 text-xs uppercase tracking-widest2 text-muteText">
            (02) Portfólio
          </p>
          <div className="md:col-span-9">
            <h2
              data-testid="portfolio-title"
              className="font-serif text-4xl sm:text-5xl md:text-6xl text-ink tracking-tight leading-[1.02]"
            >
              Obras <span className="italic text-olive">recentes</span>.
            </h2>
            <p className="mt-6 max-w-xl text-base sm:text-lg font-light leading-relaxed text-inkSoft">
              Uma seleção de projetos pelo país, onde cada planta,
              pedra e linha são escolhidas com intenção.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-4 md:gap-6 auto-rows-auto">
          {PORTFOLIO.map((p, i) => (
            <figure
              key={i}
              data-testid={`portfolio-item-${i}`}
              className={`relative overflow-hidden group reveal ${p.span}`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className={`overflow-hidden ${p.ratio} w-full`}>
                <img
                  src={p.src}
                  alt={p.title}
                  className="w-full h-full object-cover photo-edit transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                />
              </div>
              <figcaption className="mt-4 flex items-start justify-between">
                <div>
                  <p className="font-serif text-lg md:text-xl text-ink">
                    {p.title}
                  </p>
                  <p className="text-xs uppercase tracking-widest2 text-muteText mt-1">
                    {p.place}
                  </p>
                </div>
                <span className="font-serif italic text-muteText text-sm">
                  0{i + 1}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

const Sobre = () => {
  useReveal();
  return (
    <section
      id="sobre"
      data-testid="about-section"
      className="py-24 md:py-40 border-t border-hairline"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12">
          <p className="md:col-span-3 text-xs uppercase tracking-widest2 text-muteText reveal">
            (03) Sobre
          </p>

          <div className="md:col-span-9 reveal">
            <h2
              data-testid="about-title"
              className="font-serif text-4xl sm:text-5xl md:text-[64px] text-ink tracking-tight leading-[1.05] max-w-4xl"
            >
              O <span className="italic text-olive">MRjardins</span> nasceu da
              vontade de cuidar de espaços verdes com tempo, atenção e respeito.
            </h2>

            <div className="mt-12 grid md:grid-cols-2 gap-10 max-w-4xl">
              <p
                data-testid="about-paragraph-1"
                className="text-base sm:text-lg font-light leading-relaxed text-inkSoft"
              >
                Sediados em Évora, no coração do Alentejo, e a operar em todo o
                território nacional — mediante consulta e orçamento prévio.
                Cuidamos de jardins residenciais, quintas e projetos
                paisagísticos com a mesma dedicação artesanal em cada metro
                quadrado.
              </p>
              <p
                data-testid="about-paragraph-2"
                className="text-base sm:text-lg font-light leading-relaxed text-inkSoft"
              >
                Aliamos o conhecimento técnico ao olhar estético — porque um
                bom jardim não se faz só com plantas, faz-se com tempo,
                proporção e silêncio.
              </p>
            </div>

            <div className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-8 border-t border-hairline pt-10">
              {[
                { k: "5+", v: "Anos de experiência" },
                { k: "100+", v: "Clientes satisfeitos" },
                { k: "Nacional", v: "Área de atuação" },
                { k: "4", v: "Áreas de serviço" },
              ].map((stat, i) => (
                <div key={i} data-testid={`about-stat-${i}`}>
                  <p className="font-serif text-4xl md:text-5xl text-ink">
                    {stat.k}
                  </p>
                  <p className="text-xs uppercase tracking-widest2 text-muteText mt-2">
                    {stat.v}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Contactos = () => (
  <section
    id="contactos"
    data-testid="contact-section"
    className="bg-ink text-bone py-24 md:py-40"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <p className="text-xs uppercase tracking-widest2 text-bone/60">
        (04) Contactos
      </p>

      <h2
        data-testid="contact-headline"
        className="font-serif mt-6 text-5xl sm:text-6xl md:text-[88px] leading-[0.98] tracking-tight max-w-5xl"
      >
        Vamos transformar
        <br />
        o <span className="italic text-sage">seu jardim</span>.
      </h2>

      <div className="mt-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7 grid sm:grid-cols-2 gap-10">
          <a
            href={PHONE_HREF}
            data-testid="contact-phone"
            className="group block border-t border-bone/20 pt-6"
          >
            <div className="flex items-center gap-3 text-bone/60">
              <Phone className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-widest2">Telefone</span>
            </div>
            <p
              data-testid="contact-phone-text"
              className="font-serif text-3xl md:text-4xl mt-4 group-hover:text-sage transition-colors duration-500"
            >
              968 471 110
            </p>
          </a>

          <a
            href={EMAIL_HREF}
            data-testid="contact-email"
            className="group block border-t border-bone/20 pt-6"
          >
            <div className="flex items-center gap-3 text-bone/60">
              <Mail className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-widest2">Email</span>
            </div>
            <p
              data-testid="contact-email-text"
              className="font-serif text-2xl md:text-[28px] mt-4 group-hover:text-sage transition-colors duration-500 break-words"
            >
              jardim.mr@hotmail.com
            </p>
          </a>

          <div
            data-testid="contact-location"
            className="border-t border-bone/20 pt-6"
          >
            <div className="flex items-center gap-3 text-bone/60">
              <MapPin className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-widest2">Zona</span>
            </div>
            <p
              data-testid="contact-location-text"
              className="font-serif text-2xl md:text-[28px] mt-4"
            >
              Évora · Portugal · Cobertura Nacional
            </p>
          </div>

          <div className="border-t border-bone/20 pt-6">
            <div className="flex items-center gap-3 text-bone/60">
              <span className="text-xs uppercase tracking-widest2">Horário</span>
            </div>
            <p className="font-serif text-2xl md:text-[28px] mt-4">
              Seg — Sáb · 08h00 — 19h00
            </p>
          </div>
        </div>

        <div className="md:col-span-5 md:pl-10 md:border-l md:border-bone/20 flex flex-col justify-between gap-10">
          <p className="text-base md:text-lg font-light leading-relaxed text-bone/80">
            Estamos disponíveis em todo o país, mediante consulta e orçamento
            prévio. Visitamos o seu espaço, ouvimos as suas ideias e preparamos
            uma proposta sem compromisso.
          </p>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-cta"
            className="inline-flex items-center justify-between w-full md:w-auto gap-6 bg-bone text-ink px-8 py-5 text-sm uppercase tracking-widest2 hover:bg-sage transition-colors duration-500"
          >
            Falar pelo WhatsApp
            <ArrowUpRight className="w-5 h-5" strokeWidth={1.6} />
          </a>
        </div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer data-testid="site-footer" className="bg-ink text-bone/60 border-t border-bone/10">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
      <div className="flex items-center gap-2">
        <img src={LOGO_URL} alt="MRjardins logo" className="w-8 h-8 object-contain" />
        <span className="font-serif text-lg text-bone">
          MR<span className="italic text-sage">jardins</span>
        </span>
      </div>
      <p className="text-xs uppercase tracking-widest2">
        © {new Date().getFullYear()} MRjardins — Évora · Cobertura Nacional
      </p>
      <p className="text-xs uppercase tracking-widest2">
        Feito com cuidado, no Alentejo.
      </p>
    </div>
  </footer>
);

/* ---------- Home ---------- */

const Home = () => {
  return (
    <main className="bg-bone text-inkSoft" data-testid="page-home">
      <Header />
      <Hero />
      <Servicos />
      <Portfolio />
      <Sobre />
      <Contactos />
      <Footer />
    </main>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
